<template>
  <div class="apw">
    <transition name="apw-fade">
      <div v-show="open" class="apw-panel">
        <ask-peter-scoped :user-selection="userSelection" :user-details="userDetails" />
      </div>
    </transition>

    <!-- First-visit greeting, next to the mascot. Goes away on its own or on click. -->
    <transition name="apw-fade">
      <div v-if="greeting && !open" class="apw-greeting" @click="toggle">Hi, I'm Peter. Ask me about PGP or the product data</div>
    </transition>

    <!-- The mascot IS the launcher: Peter the owl floats in the corner and hops to say hello (first sight
         and on hover); when the panel is open he gives way to a small close button. -->
    <button class="apw-launcher" :class="{ 'apw-open': open, 'apw-wave': waving }" @click="toggle"
      @mouseenter="wave" :aria-label="open ? 'Close Peter' : 'Ask Peter'" title="Ask Peter about PGP or the product data">
      <!-- Owl poses live in one sprite strip (assets/images/peter-owl-sprite.png): the first is the resting
           pose; the hops play only while waving, so it never loops forever in the corner. -->
      <span class="apw-mascot apw-sprite" aria-hidden="true" />
      <svg class="apw-mascot apw-close" viewBox="0 0 96 96" aria-hidden="true">
        <circle cx="48" cy="48" r="30" fill="#1E2A78" />
        <g stroke="#fff" stroke-width="5" stroke-linecap="round">
          <line x1="36" y1="36" x2="60" y2="60" /><line x1="60" y1="36" x2="36" y2="60" />
        </g>
      </svg>
    </button>
  </div>
</template>

<script>
import AskPeterScoped from './AskPeterScoped.vue';

// Floating launcher + panel for the Peter chat. Mounted once in the app shell so it floats on every
// screen. Runs inside the authenticated session, so the /converse call authenticates by cookie. The
// panel is hidden, not destroyed, while minimised so both conversations survive.
export default {
  name: 'AskPeterWidget',
  components: { AskPeterScoped },
  props: ['userSelection', 'userDetails'],
  data: function () {
    return { open: false, waving: false, greeting: false, waveTimer: null, greetTimer: null };
  },
  mounted: function () {
    // Pilot users get this widget INSTEAD of the Voiceflow FAQ bubble (both sit bottom-right). The
    // Voiceflow host element lives in the light DOM, so a class on <html> is enough to hide it.
    document.documentElement.classList.add('apw-active');
    // First sight this browser session: hop and say hello once, then stay quiet.
    var seen = false;
    try { seen = !!window.sessionStorage.getItem('apw-greeted'); window.sessionStorage.setItem('apw-greeted', '1'); } catch (err) { /* storage blocked */ }
    if (!seen) {
      var that = this;
      window.setTimeout(function () { that.wave(); that.greeting = true; }, 900);
      this.greetTimer = window.setTimeout(function () { that.greeting = false; }, 9000);
    }
  },
  beforeUnmount: function () {
    document.documentElement.classList.remove('apw-active');
    window.clearTimeout(this.waveTimer);
    window.clearTimeout(this.greetTimer);
  },
  methods: {
    toggle: function () {
      this.open = !this.open;
      this.greeting = false;
    },
    // Restart the hop animation (a class toggle so it can replay on every hover). 2000ms = the CSS run.
    wave: function () {
      if (this.open) return;
      var that = this;
      this.waving = false;
      window.clearTimeout(this.waveTimer);
      window.requestAnimationFrame(function () { that.waving = true; });
      this.waveTimer = window.setTimeout(function () { that.waving = false; }, 2000);
    },
  },
};
</script>

<style>
/* Hide the Voiceflow FAQ bubble while the widget is mounted (see mounted/beforeUnmount). */
html.apw-active #voiceflow-chat { display: none !important; }
</style>

<style scoped>
/* The owl floats in the corner on the page itself (no disc, a soft shadow under the drawing); open =
   a small navy close button in the usual spot. */
.apw-launcher { position: fixed; right: 20px; bottom: 36px; z-index: 2147482000;
  width: 120px; height: 120px; padding: 0; border: none; border-radius: 50%; cursor: pointer; background: transparent;
  display: inline-flex; align-items: center; justify-content: center; transition: transform .18s, width .18s, height .18s; }
.apw-launcher:hover { transform: translateY(-2px); }
.apw-launcher:focus-visible { outline: 3px solid #00A76F; outline-offset: 2px; }
.apw-launcher.apw-open { width: 48px; height: 48px; bottom: 16px; }
.apw-mascot { width: 112px; height: 112px; }
.apw-launcher.apw-open .apw-mascot { width: 40px; height: 40px; }
.apw-launcher.apw-open .apw-sprite, .apw-close { display: none; }
.apw-launcher.apw-open .apw-close { display: block; }

/* Sprite strip: four poses side by side - rest | half | open | up (each 1/4 of the width). Resting = the
   first pose. A wave is two hops of 1s: crouch, wings lift half then open, up at the apex, open and half on
   the way down, rest on landing. Pose switches are step changes; the movement itself is a CSS translate,
   so it stays smooth at any size. */
.apw-sprite { display: inline-block; background: url('../assets/images/peter-owl-sprite.png') 0 0 / 400% 100% no-repeat;
  filter: drop-shadow(0 6px 10px rgba(30, 42, 120, 0.35)); }
.apw-wave .apw-sprite { animation: apw-pose 1s step-end 2, apw-hop 1s ease-in-out 2; }
@keyframes apw-pose {
  0%, 100% { background-position: 0 0; }          /* rest */
  12% { background-position: 33.333% 0; }         /* half: wings start to lift */
  26% { background-position: 66.667% 0; }         /* open: take-off */
  40% { background-position: 100% 0; }            /* up: apex */
  58% { background-position: 66.667% 0; }         /* open: gliding down */
  74% { background-position: 33.333% 0; }         /* half */
  88% { background-position: 0 0; }               /* landed */
}
@keyframes apw-hop {
  0%, 100% { transform: translateY(0); }
  12% { transform: translateY(3px); }         /* crouch */
  50% { transform: translateY(-20px); }       /* apex */
  88% { transform: translateY(0); }
}

/* Greeting bubble to the left of the mascot. */
.apw-greeting { position: fixed; right: 152px; bottom: 60px; z-index: 2147482000; white-space: nowrap;
  background: #fff; color: #1E2A78; font-family: 'Inter', system-ui, sans-serif; font-size: 12px; font-weight: 600;
  line-height: 1.3; padding: 8px 12px; border-radius: 12px 12px 4px 12px; cursor: pointer;
  box-shadow: 0 8px 22px rgba(30, 42, 120, 0.25); }
.apw-greeting::after { content: ''; position: absolute; right: -6px; bottom: 10px; border: 6px solid transparent; border-left-color: #fff; }

/* Tall panel anchored bottom-right: its top clears the 80px app header, its bottom the close button. */
.apw-panel { position: fixed; right: 20px; top: 92px; bottom: 76px; z-index: 2147482000;
  width: 520px; max-width: calc(100vw - 28px);
  background: #1c1c1e; border: 1px solid #2e2e34; border-radius: 18px; overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5); display: flex; flex-direction: column; }

.apw-fade-enter-active, .apw-fade-leave-active { transition: opacity .18s ease, transform .18s ease; }
.apw-fade-enter-from, .apw-fade-leave-to { opacity: 0; transform: translateY(8px); }

@media (prefers-reduced-motion: reduce) {
  .apw-wave .apw-sprite { animation: none; }
}
</style>
